import {
  CellSelection, // @ts-ignore (incorrect type)
  __pastedCells,
  cellAround, // @ts-ignore (incorrect type)
  handlePaste as proseMirrorHandlePaste,
  selectedRect,
} from '@_ueberdosis/prosemirror-tables';
import { TableMap } from '@_ueberdosis/prosemirror-tables';
import { Attributes, Editor, JSONContent, findParentNodeClosestToPos, getMarkType } from '@tiptap/core';
import { TABLE_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import cloneDeep from 'lodash.clonedeep';
import { nanoid } from 'nanoid';
import { joinBackward as _joinBackward } from 'prosemirror-commands';
import { Attrs, Mark, NodeType, Node as ProseMirrorNode, Schema, Slice } from 'prosemirror-model';
import { EditorState, Selection, TextSelection, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { WidgetType } from 'types/widget.types';
import { toHex } from 'utils/color';
import { parseStrictNumber } from 'utils/number';

import { TEXT_TAG_TO_STYLES_MAP } from '../common/TextBasedWidgets.config';
import { getNearestMarks, isTextStyleMark } from '../common/TextBasedWidgets.helpers';
import { TextWidgetTag } from '../common/TextBasedWidgets.types';
import { CellAttributes } from './TableWidget.types';
import { NodeFragment } from './TableWidget.types';
import { VERSION } from './TableWidget.upgrade';
import { DEFAULT_HEIGHT_PX, DEFAULT_TABLE_DATA, DEFAULT_WIDTH_PX } from './defaultTableData';

type TextStyle = { fontFamily?: string; fontSize?: number; color?: string };

const TABLE_EXT_NAME = 'table';
const TABLE_HEADER_EXT_NAME = 'tableHeader';
const TABLE_CELL_EXT_NAME = 'tableCell';

export const DEFAULT_COL_WIDTH = 80;

/**
 * Return minimum default table data
 *
 * @returns default table widget data
 */
export const generateDefaultData = (content: JSONContent | string = DEFAULT_TABLE_DATA) => {
  return {
    widgetType: WidgetType.Table,
    widgetData: {
      version: VERSION,
      topPx: 10,
      leftPx: 20,
      widthPx: DEFAULT_WIDTH_PX,
      heightPx: DEFAULT_HEIGHT_PX,
      rotateDeg: 0,
      proseMirrorData: content,
    },
  };
};

/**
 * Find all text content node and return in flat array
 *
 * @param node - A prosemirror content node
 * @returns
 */
const getAllTextContents = (node: JSONContent): JSONContent[] => {
  const contents: JSONContent[] = [];

  /**
   * Internal helper to find text content node recursively
   *
   * @param content - Content node in json format
   * @returns
   */
  const findText = (content: JSONContent): void => {
    if (Array.isArray(content?.content) && content?.content.length) {
      return content?.content.forEach(findText);
    }

    if (content?.type === 'text') contents.push(content);
  };

  findText(node);

  return contents;
};

/**
 * This is greedy fast check. The highest in use color will be set as dominant color.
 * Later on, we can improve this by filtering most length text contents.
 *
 * @param node
 * @returns
 */
const getDominantTextStyleByColor = (node: JSONContent): TextStyle | undefined => {
  const styles = new Map<string, Array<Record<string, any>>>();
  const textContents = getAllTextContents(node);

  textContents?.forEach(({ marks }) => {
    marks?.forEach((mark) => {
      if (mark?.type !== 'textStyle') return;

      if (mark?.attrs?.color?.length) {
        const style = styles.get(mark.attrs.color);
        const record = {
          fontFamily: mark.attrs?.fontFamily,
          fontSize: parseStrictNumber(mark.attrs?.fontSize),
          color: mark.attrs?.color,
        };

        if (Array.isArray(style) && style.length) {
          // Add to existing lists
          style.push(record);
        } else {
          // Add new record if not exist
          styles.set(mark.attrs.color, [record]);
        }
      }
    });
  });

  /**
   * Sort map by frequent color count.
   * This can be improved by
   */
  const result = [...styles.values()].sort((a, b) => a.length - b.length).pop();

  return result?.shift();
};

/**
 * Get table cell position information. This can be used with TableMap for more complex computation
 *
 * @param doc - Prosemirror document node
 * @returns
 */
const getTableData = (doc: ProseMirrorNode): { cellPositions: number[]; numRows: number; numCols: number } => {
  const cellPositions = [] as number[];
  let numRows = 0;

  doc.descendants((node: ProseMirrorNode, pos: number) => {
    const { name } = node.type;
    if (name === 'tableRow') numRows++;
    if (name === TABLE_CELL_EXT_NAME || name === TABLE_HEADER_EXT_NAME) cellPositions.push(pos);
  });

  const numCols = cellPositions.length / numRows;

  return { cellPositions, numRows, numCols };
};

/**
 * Get selected table node from document
 *
 * @param selection - A prosemirror selection instance
 * @returns
 */
const getClosestTable = (selection: Selection): ReturnType<typeof findParentNodeClosestToPos> =>
  findParentNodeClosestToPos(selection.$anchor, (node: ProseMirrorNode) => node.type.name === TABLE_EXT_NAME);

/**
 * Get selected closest cell node attributes if exists
 *
 * @param selection - A prosemirror selection instance
 * @returns
 */
const getClosestCellAttributes = (selection: Selection): Attributes => {
  const cellNode = getClosestCell(selection);
  return cellNode?.node?.attrs ?? {};
};

/**
 * Apply giving border style to all table cells. If no style provides in the arguments,
 * it will choose current selection nearest cell node style instead.
 *
 * @param editor - A tiptap editor instance
 * @param style - A style attributes
 */
const updateAllCellBorderStyle = (
  state: EditorState,
  tr: Transaction,
  dispatch: (tr: Transaction) => void,
  style?: Attributes,
) => {
  if (!dispatch) return;

  const { selection, doc } = state;
  const { borderWidth, borderColor } = (style ?? getClosestCellAttributes(selection)) as CellAttributes;

  doc.descendants((node: ProseMirrorNode, pos) => {
    if (node.type.name === TABLE_CELL_EXT_NAME || node.type.name === TABLE_HEADER_EXT_NAME) {
      if (node.attrs?.borderWidth !== borderWidth || node.attrs?.borderColor !== borderColor) {
        tr.setNodeMarkup(pos, null, { borderWidth, borderColor });
      }
    }
  });

  dispatch(tr);
};

enum Direction {
  /**
   * In prosemirror direction control, -1 means to left/above
   */
  before = -1,

  /**
   * In prosemirror direction control, 1 means to right/below
   */
  after = 1,
}

enum Orientation {
  horizontal = 'horiz',
  vertical = 'vert',
}

/**
 * Check whether giving node is table cell node or not
 *
 * @param node - A prosemirror node
 * @returns
 */
const isCellNode = (node?: ProseMirrorNode) =>
  node && [TABLE_CELL_EXT_NAME, TABLE_HEADER_EXT_NAME].some((name) => node.type.name === name);

/**
 * Apply current selection column style attributes to next column on left or right
 *
 * @param state - Prosemirror state
 * @param tr - Prosemirror transaction
 * @param dispatch - Prosemirror transaction dispatch caller
 * @param direction -
 * @returns
 */
const updateCellAttrs = (
  state: EditorState,
  tr: Transaction,
  dispatch: (tr: Transaction) => void,
  direction: Direction,
  orientation: Orientation,
) => {
  if (!dispatch) return;

  const { selection } = state;
  const { node: tableNode } = getClosestTable(selection) ?? {};
  const { pos: cellPos } = getClosestCell(selection) ?? {};

  if (!tableNode || typeof cellPos !== 'number') return;

  const tableMap = TableMap.get(tableNode);
  const mapCellPos = cellPos - 1;

  if (!tableMap.map.includes(mapCellPos)) return;

  try {
    // left column count from selected cell column
    const cellIndex = tableMap.map.indexOf(mapCellPos);
    const row = Math.floor(cellIndex / tableMap.width);
    const col = cellIndex % tableMap.width;
    const isColumn = orientation === Orientation.horizontal;

    const addingAxis = orientation === Orientation.horizontal ? tableMap.height : tableMap.width;

    for (let index = 0; index < addingAxis; index++) {
      const position: [number, number] = isColumn ? [index, col] : [row, index];
      const sourceCellPos = tableMap.positionAt(...position, tableNode);
      const sourceCellNode = state.doc.nodeAt(sourceCellPos + 1);

      if (!sourceCellNode || !isCellNode(sourceCellNode)) continue;

      const nextCellPos = tableMap.nextCell(sourceCellPos, orientation, direction);
      const nextCellNode = state.doc.nodeAt(nextCellPos + 1);

      if (!nextCellNode || !isCellNode(nextCellNode)) continue;

      // Copy source node attributes to target column node
      tr.setNodeMarkup(nextCellPos + 1, null, { ...sourceCellNode?.attrs });
    }

    dispatch(tr);
  } catch (error) {
    // Safe to ignore error if update fails
    console.warn(error);
  }
};

const updateColumnAttrsLeft = (state: EditorState, tr: Transaction, dispatch: (tr: Transaction) => void) =>
  updateCellAttrs(state, tr, dispatch, Direction.before, Orientation.horizontal);
const updateColumnAttrsRight = (state: EditorState, tr: Transaction, dispatch: (tr: Transaction) => void) =>
  updateCellAttrs(state, tr, dispatch, Direction.after, Orientation.horizontal);
const updateRowAttrsBelow = (state: EditorState, tr: Transaction, dispatch: (tr: Transaction) => void) =>
  updateCellAttrs(state, tr, dispatch, Direction.after, Orientation.vertical);
const updateRowAttrsAbove = (state: EditorState, tr: Transaction, dispatch: (tr: Transaction) => void) =>
  updateCellAttrs(state, tr, dispatch, Direction.before, Orientation.vertical);

/**
 * Recursively extracts a list of marks and attributes from text and paragraph nodes in the given node
 * @param node node to scan
 * @returns styles and attributes
 */
const getStyleData = (node: ProseMirrorNode | null): { paragraphAttrs: Attrs; marks: readonly Mark[] } => {
  let marks: readonly Mark[] = [];
  let paragraphAttrs: Attrs = {};

  if (!node?.firstChild) return { marks, paragraphAttrs };

  const childNode = node.firstChild;
  if (childNode?.type.name === 'paragraph') {
    paragraphAttrs = childNode.attrs;
    const grandchildNode = childNode.firstChild;
    if (grandchildNode) marks = grandchildNode.marks;
    return { paragraphAttrs, marks };
  } else {
    return getStyleData(childNode);
  }
};

/**
 * This function iterates through the marks on a ProseMirror text node.
 * If the node contains a or is missing a textStyle mark, it populates missing attributes
 * with default or nearby selection styles.
 *
 * @param {ProseMirrorNode} node - The node on which to set or fill the textStyle mark.
 * @param {EditorView} view - The ProseMirror EditorView, used to get the current state and schema.
 *
 */
const fillMissingAttributes = (node: ProseMirrorNode, view: EditorView) => {
  if (node.isText) {
    node.marks.forEach((mark) => {
      if (isTextStyleMark(mark)) {
        const { fontSize, fontFamily, color } = mark.attrs;
        const paragraphStyles = TEXT_TAG_TO_STYLES_MAP[TextWidgetTag.Paragraph];
        const newAttrs = {
          fontSize: fontSize || paragraphStyles.fontSize + 'px',
          fontFamily: fontFamily || paragraphStyles.fontFamily,
          color: color || paragraphStyles.color,
        };

        (mark.attrs as Attrs) = newAttrs;
      }
    });

    // if the text node is missing the textStyle mark add it
    if (!node.marks.find(isTextStyleMark)) {
      let newMarks: Mark[] = [...node.marks];

      const textStyleMarkType = getMarkType('textStyle', view.state.schema);

      // get the nearest text style mark from the selection
      const nearestMarks = getNearestMarksFromSelection(view.state);
      const nearestTextStyle = nearestMarks.find((mark) => mark.type.name === 'textStyle');

      // in case there are no nearest style marks from the selection, use the default paragraph styles as a fallback
      const fallbackDefaultStyle = TEXT_TAG_TO_STYLES_MAP[TextWidgetTag.Paragraph];

      // the attributes for the text style mark
      const textStyleAttrs = { ...nearestTextStyle?.attrs } || {
        fontSize: fallbackDefaultStyle.fontSize + 'px',
        fontFamily: fallbackDefaultStyle.fontFamily,
        color: fallbackDefaultStyle.color,
      };

      // add the new text style mark to the list of marks
      const newTextStyleMark = textStyleMarkType.create(textStyleAttrs);
      newMarks.push(newTextStyleMark);

      // Force update the marks on the pasted slice text node
      // @ts-ignore
      node.marks = newMarks;
    }
  } else {
    node.content.forEach((child) => fillMissingAttributes(child, view));
  }
};

/**
 * Updaet the checkbox ids in a node
 *
 * @param {ProseMirrorNode} node - The node on which to update the ids.
 *
 */
const updateCheckboxIds = (node: ProseMirrorNode) => {
  if (node.type.name === 'checkbox') {
    // Force update ccheckbox id
    // @ts-ignore
    (node.attrs as Attrs) = { ...node.attrs, id: nanoid() };
  } else if (node.content) {
    node.content.forEach((child) => updateCheckboxIds(child));
  }
};

/**
 * Handles paste events for copied text content in tables
 * @param view the editor view
 * @param e the paste event
 * @param slice the content to be pasted
 * @returns
 */
const handleTextPaste = (view: EditorView, e: ClipboardEvent, slice: Slice) => {
  const { numCols } = getTableData(view.state.doc);
  const originalStyles: {
    index: number;
    currentRow: number;
    currentColumn: number;
    cellAttrs: Attrs;
    paragraphAttrs: Attrs;
    marks: readonly Mark[];
    type: NodeType;
  }[] = [];
  const originalSelectionBox = selectedRect(view.state);

  slice.content.forEach((node) => {
    fillMissingAttributes(node, view);
    updateCheckboxIds(node);
  });

  originalSelectionBox.map.map.forEach((pos, index) => {
    const { left, top, right, bottom, tableStart } = originalSelectionBox;
    const currentColumn = index % numCols;
    const currentRow = Math.floor(index / numCols);

    // ignore cells that are outside of the selection
    if (currentColumn < left || currentColumn >= right || currentRow < top || currentRow >= bottom) return;

    const cell = view.state.doc.nodeAt(pos + tableStart);

    if (cell) {
      const type = cell.type;
      const { backgroundColor, borderColor, borderWidth } = cell.attrs;
      const cellAttrs = {
        backgroundColor,
        borderColor,
        borderWidth,
      };
      const paragraph = cell.firstChild;
      const paragraphAttrs = paragraph?.attrs || [];
      const marks = paragraph?.firstChild?.marks || [];

      originalStyles.push({ index, currentRow, currentColumn, cellAttrs, paragraphAttrs, marks, type });
    }
  });

  const result = proseMirrorHandlePaste(view, e, slice);

  if (result) {
    const tr = view.state.tr;
    const newSelectionBox = selectedRect(view.state);

    newSelectionBox.map.map.forEach((pos, index) => {
      const { left, top, right, bottom, tableStart } = newSelectionBox;
      const currentCol = index % numCols;
      const currentRow = Math.floor(index / numCols);

      // ignore cells that are outside of the selection
      if (currentCol < left || currentCol >= right || currentRow < top || currentRow >= bottom) return;

      const styles = originalStyles.find((style) => style.index === index);
      const cellPos = pos + tableStart;
      const cell = view.state.doc.nodeAt(cellPos);

      if (styles && cell) {
        const { cellAttrs, paragraphAttrs, type } = styles;
        let { marks } = styles;
        tr.setNodeMarkup(cellPos, type, Object.assign({}, cell.attrs, cellAttrs));

        cell.descendants((node, nodePos) => {
          const absolutePosition = nodePos + cellPos + tableStart;
          if (node.type.name === 'paragraph') {
            tr.setNodeMarkup(absolutePosition, null, paragraphAttrs);
          }
          if (node.type.name === 'checkbox') {
            tr.setNodeMarkup(absolutePosition, null, { ...node.attrs, id: nanoid() });
          }
          if (node.isText) {
            if (!marks.length) {
              const originalRowStyles = originalStyles.filter((style) => style.currentRow === currentRow);
              marks =
                originalRowStyles[originalSelectionBox.map.width - 1]?.marks ||
                getNearestMarks(view.state, absolutePosition);
            }
            if (marks.length) {
              tr.removeMark(absolutePosition, absolutePosition + node.textContent.length);
              marks.forEach((mark) => {
                tr.addMark(absolutePosition, absolutePosition + node.textContent.length, mark);
              });
            }
          }
        });
      }
    });

    // Remove intermediate history step from transaction to avoid double undo
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
  }

  return result;
};

/**
 * Retrieves the nearest cell marks for a given row index in the table.
 * If there are no marks in the row, it will look at the rows above and below.
 *
 * @param rowIdx - The index of the row.
 * @param state - The editor state.
 * @returns An array of cell marks.
 */
const getNearestRowCellMarks = (rowIdx: number, state: EditorState, currentColumn: number) => {
  const rows: ProseMirrorNode[] = [];

  // table row will only look for marks in the first row
  // body rows will not look for marks in the header row
  const isHeader = rowIdx === 0;

  // get all the rows from the table doc
  state.doc.descendants((node) => {
    if (node.type.name === 'tableRow') rows.push(cloneDeep(node));
  });

  // iterate over the rows to find the nearest cell marks for the given row index
  // use the marks from the same column
  let cellMarksSameColumn: readonly Mark[] = [];
  let cellParagraphAttrsSameColumn: Attrs = {};

  // else use the new cell marks from a different column
  let cellMarks: readonly Mark[] = [];
  let cellParagraphAttrs: Attrs = {};

  // first look at the same row and the rows above
  for (let i = rowIdx; i >= 0; i--) {
    // stop at the header row except for the header row itself
    if (i === 0 && !isHeader) break;
    if (rowIdx === 1) break;

    const row = rows[i];
    const rowFragment = row?.content as NodeFragment;
    if (!rowFragment) continue;

    const rowContent = rowFragment?.content;
    if (!rowContent || !rowContent.length) continue;

    // iterate over the row content
    for (let j = 0; j < rowContent.length; j++) {
      const cell = rowContent[j];
      if (!cell) continue;

      const { marks, paragraphAttrs } = getStyleData(cell);
      if (marks.length) {
        if (j === currentColumn) {
          cellMarksSameColumn = marks;
          cellParagraphAttrsSameColumn = paragraphAttrs;
          // we found the marks we were looking for - stop
          break;
        }
        cellMarks = marks;
        cellParagraphAttrs = paragraphAttrs;
      }
    }

    if (cellMarksSameColumn.length) break;
    // only check the header row for header cells
    if (isHeader) break;
  }

  // If no marks on the rows above - look at the rows below
  if (!cellMarksSameColumn.length) {
    for (let i = rowIdx; i < rows.length; i++) {
      const row = rows[i];
      const rowFragment = row?.content as NodeFragment;
      if (!rowFragment) continue;

      const rowContent = rowFragment?.content;
      if (!rowContent || !rowContent.length) continue;

      // iterate over the row content
      for (let j = 0; j < rowContent.length; j++) {
        const cell = rowContent[j];
        if (!cell) continue;

        const { marks, paragraphAttrs } = getStyleData(cell);
        if (marks.length) {
          if (j === currentColumn) {
            cellMarksSameColumn = marks;
            cellParagraphAttrsSameColumn = paragraphAttrs;
            // we found the marks we were looking for - stop
            break;
          }
          cellMarks = marks;
          cellParagraphAttrs = paragraphAttrs;
        }
      }

      if (cellMarksSameColumn.length) break;
      // only check the header row for header cells
      if (isHeader) break;
    }
  }

  // if there's still no marks - return the default table text marks (e.g. all rows are empty)
  if (!cellMarks.length && !cellMarksSameColumn.length) {
    const textStyle = TEXT_TAG_TO_STYLES_MAP[TextWidgetTag.Paragraph];
    cellMarks = [
      state.schema.marks.textStyle.create({
        fontSize: `${textStyle.fontSize}px`,
        color: textStyle.color,
        fontFamily: textStyle.fontFamily,
      }),
    ];
  }

  const newCellMarks = cellMarksSameColumn.length ? cellMarksSameColumn : cellMarks;
  const newCellParagraphAttrs = { ...cellParagraphAttrs, ...cellParagraphAttrsSameColumn };

  return { cellMarks: newCellMarks, cellParagraphAttrs: newCellParagraphAttrs };
};

/**
 * Process the pm paste event to ensure that checkboxes have unique ids
 *
 * @param view Editor view after pm paste event
 * @param pastedWidth number cols pasted
 * @param pastedHeight # rows pasted
 */
const processCheckboxPaste = (view: EditorView, pastedWidth: number, pastedHeight: number) => {
  // Traverse pasted table
  // For each checkbox, set a new id
  let newSelectionBox = selectedRect(view.state);
  const tr = view.state.tr;
  const { tableStart, table, left, top } = newSelectionBox;
  const { map: newMap, width: newWidth } = newSelectionBox.map;
  table.content.forEach((row, _, rowIndex) => {
    if (rowIndex < top || rowIndex >= top + pastedHeight) return;
    row.content.forEach((cell, _, columnIndex) => {
      if (columnIndex < left || columnIndex >= left + pastedWidth) return;
      const cellPos = newMap[rowIndex * newWidth + columnIndex] + tableStart;
      cell.descendants((node, pos) => {
        const absolutePosition = pos + cellPos + tableStart;
        if (node.type.name === 'checkbox') {
          tr.setNodeMarkup(absolutePosition, null, { ...node.attrs, id: nanoid() });
        }
      });
    });
  });

  // Remove intermediate history step from transaction to avoid double undo
  tr.setMeta('addToHistory', false);
  view.dispatch(tr);
};

/**
 * Handles the paste event by applying the expected styles and attributes to the clipboard
 * @param view the editor view
 * @param e the paste event
 * @param slice the content to be pasted
 */
const handlePaste = (view: EditorView, e: ClipboardEvent, slice: Slice) => {
  const cells = __pastedCells(slice);
  const { cellPositions, numCols } = getTableData(view.state.doc);

  if (!cells) {
    return handleTextPaste(view, e, slice);
  }

  const pastedWidth = cells?.width || 1;
  const pastedHeight = cells?.height || 1;

  // if currently selecting cells, ignore that and set to a TextSelection of the first cell
  const selection = view.state.selection;
  const isCellSelection = selection instanceof CellSelection;

  if (isCellSelection) {
    const { $anchorCell, $headCell } = selection;
    const startingCellPos = $anchorCell.pos > $headCell.pos ? $headCell.pos : $anchorCell.pos;
    const newSelection = TextSelection.create(view.state.doc, startingCellPos + 2);
    view.dispatch(view.state.tr.setSelection(newSelection));
  }

  const originalSelectionBox = selectedRect(view.state);
  const { width: originalWidth, height: originalHeight } = originalSelectionBox.map;

  const cell = cellAround(view.state.selection.$from);
  const cellIndex = cellPositions.findIndex((pos) => pos === cell.pos) || 0;
  const pastingRow = Math.floor(cellIndex / numCols);
  const pastingColumn = cellIndex % numCols;

  const originalStyles: {
    pos: number;
    currentRow: number;
    currentColumn: number;
    cellAttrs: Attrs;
    paragraphAttrs: Attrs;
    marks: readonly Mark[];
    type: NodeType;
  }[] = [];

  // get all the original styles of the cells, paragraphs and text to be pasted on
  cellPositions.forEach((pos, index) => {
    const currentRow = Math.floor(index / numCols);
    const currentColumn = index % numCols;
    if (currentRow < pastingRow || currentRow >= pastingRow + pastedHeight) return;
    if (currentColumn < pastingColumn || currentColumn >= pastingColumn + pastedWidth) return;

    const cell = view.state.doc.nodeAt(pos);
    if (!cell) return;

    const { backgroundColor, borderColor, borderWidth } = cell.attrs;
    const cellAttrs = { backgroundColor, borderColor, borderWidth };
    const type = cell?.type;

    let { marks, paragraphAttrs } = getStyleData(cell);

    // if there are no marks find marks from the nearest row
    if (!marks.length) {
      const { cellMarks, cellParagraphAttrs } = getNearestRowCellMarks(currentRow, view.state, currentColumn);
      marks = cellMarks;
      paragraphAttrs = { ...paragraphAttrs, ...cellParagraphAttrs };
      // reset the font size paragraph from the marks since so that its the same as the mark
      const textStyleMark = marks.find(isTextStyleMark);
      if (textStyleMark) {
        paragraphAttrs = { ...paragraphAttrs, fontSize: textStyleMark.attrs.fontSize };
      }
    }

    originalStyles.push({
      pos,
      currentRow,
      currentColumn,
      cellAttrs,
      paragraphAttrs,
      marks,
      type,
    });
  });

  // update the slice to be pasted with the original styles
  if (!!originalStyles.length) {
    let index = 0;
    let table: Slice | ProseMirrorNode = slice;

    if (slice.content.firstChild?.type.name === 'table') table = slice.content.firstChild;

    table.content.forEach((row, rowPos, rowIndex) => {
      row.content.forEach((cell, cellPos, cellIndex) => {
        const tableIndex = pastingColumn + cellIndex;
        if (tableIndex >= originalWidth) return;

        const styles = originalStyles[index];
        if (!styles) return;

        const { cellAttrs, paragraphAttrs, type } = styles;

        (cell.attrs as Attrs) = Object.assign({}, cell.attrs, cellAttrs);
        (cell.type as NodeType) = type;
        cell.content.forEach((paragraph) => {
          (paragraph.attrs as Attrs) = paragraphAttrs;

          paragraph.content.forEach((text) => {
            if (text.isText) {
              const { marks } = styles;
              if (marks.length) (text.marks as readonly Mark[]) = marks;
            }
          });
        });

        index++;
      });
    });
  }

  const result = proseMirrorHandlePaste(view, e, slice);

  if (result) {
    // Process checkbox nodes to ensure they have unique ids
    processCheckboxPaste(view, pastedWidth, pastedHeight);

    // if there's more columns in the table after the paste, copy and apply styles from the cells to the left
    let newSelectionBox = selectedRect(view.state);
    if (newSelectionBox.map.width !== originalWidth) {
      const tr = view.state.tr;
      const { tableStart } = newSelectionBox;
      const { map: newMap, width: newWidth } = newSelectionBox.map;

      view.state.doc.content.forEach((table) => {
        table.content.forEach((row, rowOffset, rowIndex) => {
          // ignore newly added rows
          if (rowIndex >= originalHeight) return;

          const rightMostColumnIndex = originalWidth - 1 < 0 ? 0 : originalWidth - 1;
          const rightMostCell = row.child(rightMostColumnIndex);
          const { backgroundColor, borderColor, borderWidth } = rightMostCell.attrs;
          const rightMostAttrs = {
            backgroundColor,
            borderColor,
            borderWidth,
          };
          // const rightMostAttrs = rightMostCell.attrs;
          const rightMostType = rightMostCell.type;
          const styles = getStyleData(rightMostCell);
          const { paragraphAttrs } = styles;
          let marks = styles.marks;

          row.content.forEach((cell, cellOffset, columnIndex) => {
            // only check newly added columns
            if (columnIndex < originalWidth) return;
            const cellPos = newMap[rowIndex * newWidth + columnIndex] + tableStart;

            // copy styles from previous cell in row and apply to new columns
            tr.setNodeMarkup(cellPos, rightMostType, Object.assign({}, cell.attrs, rightMostAttrs));

            let newMarks = marks;
            cell.descendants((node, pos) => {
              const absolutePosition = pos + cellPos + tableStart;
              if (node.type.name === 'paragraph') {
                tr.setNodeMarkup(absolutePosition, null, paragraphAttrs);
              }
              if (node.isText) {
                if (!newMarks.length) {
                  const originalRowStyles = originalStyles.filter((style) => style.currentRow === rowIndex);
                  newMarks =
                    originalRowStyles[originalWidth - 1]?.marks || getNearestMarks(view.state, absolutePosition);
                }
                tr.removeMark(absolutePosition, absolutePosition + node.textContent.length);
                newMarks.forEach((mark) => {
                  tr.addMark(absolutePosition, absolutePosition + node.textContent.length, mark);
                });
              }
            });
          });
        });
      });

      // Remove intermediate history step from transaction to avoid double undo
      tr.setMeta('addToHistory', false);
      view.dispatch(tr);
    }

    newSelectionBox = selectedRect(view.state);
    // if there's more rows in the table after the paste, copy and apply styles from the cells above
    if (newSelectionBox.map.height !== originalHeight) {
      const tr = view.state.tr;
      const { tableStart } = newSelectionBox;
      const { map: newMap, width: newWidth } = newSelectionBox.map;
      const attrsAbove: JSONContent[] = [];

      view.state.doc.content.forEach((table) => {
        table.content.forEach((row, rowOffset, rowIndex) => {
          // only check newly added rows
          if (rowIndex < originalHeight) return;

          row.content.forEach((cell, columnOffset, columnIndex) => {
            // copy styles of cells above and apply to new cells
            const bottomRowIndex = originalHeight - 1 < 0 ? 0 : originalHeight - 1;
            const bottomMostCell = table.child(bottomRowIndex).content.child(columnIndex);
            const { backgroundColor, borderColor, borderWidth } = bottomMostCell.attrs;
            const bottomMostAttrs = attrsAbove[columnIndex] || {
              backgroundColor,
              borderColor,
              borderWidth,
            };
            // const bottomMostAttrs = attrsAbove[columnIndex] || bottomMostCell.attrs;
            const bottomMostType = bottomMostCell.type;
            const styles = getStyleData(bottomMostCell);
            const { paragraphAttrs } = styles;
            let marks = styles.marks;

            const cellPos = newMap[rowIndex * newWidth + columnIndex] + tableStart;

            // copy styles from previous cell in row and apply to new columns
            tr.setNodeMarkup(cellPos, bottomMostType, Object.assign({}, cell.attrs, bottomMostAttrs));

            let newMarks = marks;
            cell.descendants((node, pos) => {
              const absolutePosition = pos + cellPos + tableStart;
              if (node.type.name === 'paragraph') {
                tr.setNodeMarkup(absolutePosition, null, paragraphAttrs);
              }
              if (node.isText) {
                if (!newMarks.length) {
                  const originalRowStyles = originalStyles.filter((style) => style.currentRow === rowIndex);
                  newMarks =
                    originalRowStyles[originalWidth - 1]?.marks || getNearestMarks(view.state, absolutePosition);
                }
                tr.removeMark(absolutePosition, absolutePosition + node.textContent.length);
                newMarks.forEach((mark) => {
                  tr.addMark(absolutePosition, absolutePosition + node.textContent.length, mark);
                });
              }
            });
            if (!attrsAbove[columnIndex]) attrsAbove[columnIndex] = bottomMostAttrs;
          });
        });
      });

      // Remove intermediate history step from transaction to avoid double undo
      tr.setMeta('addToHistory', false);
      view.dispatch(tr);
    }

    return true;
  } else {
    return false;
  }
};

/**
 * Checks whether a given node is a list or other type of block
 * @param {object} node the node to be checked
 */
const isList = (editor: Editor, node: ProseMirrorNode) => {
  return node.type === editor.schema.nodes.bulletList || node.type === editor.schema.nodes.orderedList;
};

/**
 * Wraps the current selection in a list of the given type
 * @param type the type of list
 * @param from the position to check from
 * @param selection the entire selection
 */
const wrapInList = (editor: Editor, type: NodeType, from: number, selection: Selection, offset: number = 0) => {
  const LIST_ITEM_TYPE = editor.schema.nodes.listItem;
  let newPos = from;

  const originalTableSize = editor.view.state.doc.child(0).nodeSize;

  editor.view.state.doc.nodesBetween(from, selection.to + offset, (node, pos) => {
    if (newPos !== from) return;

    // if different list type, switch it to the given type
    if (isList(editor, node) && node.type !== type) {
      const $pos = editor.view.state.doc.resolve(pos);
      const { tr } = editor.view.state;
      tr.setNodeMarkup(pos, type);
      editor.view.dispatch(tr);
      if ($pos.nodeBefore?.type === type) {
        editor
          .chain()
          .setTextSelection(pos + 1)
          .joinBackward()
          .run();
        newPos = pos;
      }
    } else if (node.type.name === 'paragraph') {
      const $pos = editor.view.state.doc.resolve(pos);
      // if the paragraph is already in a list, ignore it
      if ($pos.parent.type === LIST_ITEM_TYPE) return;

      // if there's a list of the same type before, join this list item to it, otherwise wrap it in its own list
      wrapSingleItemInList(editor, type, $pos.pos, $pos.nodeBefore?.type === type);
      newPos = pos;
    }
  });

  const newTableSize = editor.view.state.doc.child(0).nodeSize;
  const newOffset = offset + (newTableSize - originalTableSize);

  if (newPos !== from) wrapInList(editor, type, newPos, selection, newOffset);
};

const liftListItem = (editor: Editor, pos: number) => {
  const LIST_ITEM_TYPE = editor.schema.nodes.listItem;
  editor
    .chain()
    .setTextSelection({ from: pos, to: pos + 1 })
    .liftListItem(LIST_ITEM_TYPE)
    .run();
};
const wrapSingleItemInList = (editor: Editor, type: NodeType, pos: number, joinBackward: boolean = false) => {
  editor
    .chain()
    .setTextSelection({ from: pos, to: pos + 1 })
    .wrapInList(type)
    .command(({ state, dispatch }) => {
      if (joinBackward) return _joinBackward(state, dispatch);
      return false;
    })
    .run();
};

/**
 * Toggles a list type on/off or switches one list type to another
 * @param {NodeType} type the type of list to toggle
 */
const doToggleList = (editor: Editor, type: NodeType, toList: boolean = false) => {
  const LIST_ITEM_TYPE = editor.schema.nodes.listItem;
  const { state } = editor.view;
  const { selection, doc } = state;

  // if there are any nodes that aren't in a list, wrap them in lists and ignore the other nodes
  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (node.type.name === 'paragraph') {
      const $pos = doc.resolve(pos);
      if ($pos.parent.type === LIST_ITEM_TYPE) return;
      toList = true;
    }
  });

  if (toList) {
    wrapInList(editor, type, selection.from, selection);
  } else {
    // if selection is collapsed
    if (selection instanceof TextSelection && selection.$cursor) {
      // find the nearest parent list from cursor position
      const $cursor = selection.$cursor;
      let parentList: ProseMirrorNode = $cursor.node($cursor.depth);
      let listItem: ProseMirrorNode = $cursor.node($cursor.depth);
      let textNodePos = $cursor.pos - $cursor.parentOffset;
      let listDepth, listItemDepth;
      for (listDepth = $cursor.depth - 1; listDepth >= 1 && !isList(editor, parentList); listDepth--) {
        parentList = $cursor.node(listDepth);
      }
      for (listItemDepth = $cursor.depth - 1; listItemDepth >= 1 && listItem.type !== LIST_ITEM_TYPE; listItemDepth--) {
        listItem = $cursor.node(listItemDepth);
      }

      let listPos = -1;
      editor.view.state.doc.descendants((node, pos) => {
        if (node === parentList) listPos = pos;
      });

      // switch the parent list to the new type, or lift all its items out of it
      if (isList(editor, parentList) && parentList.type !== type) {
        if (listDepth >= 4 || listItem.childCount > 1) {
          const { tr } = editor.view.state;
          tr.setNodeMarkup(listPos, type);
          editor.view.dispatch(tr);
        } else {
          liftListItem(editor, textNodePos);
          wrapSingleItemInList(editor, type, textNodePos);
        }
      } else {
        liftListItem(editor, textNodePos);
      }
    } else {
      let offset = 0;
      editor.view.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
        const originalDocSize = editor.view.state.doc.nodeSize;
        if (node.type.name === 'paragraph') {
          const $node = editor.view.state.doc.resolve(pos + offset);

          let parentList: ProseMirrorNode = $node.node($node.depth);
          let listDepth;
          for (listDepth = $node.depth - 1; listDepth >= 1 && !isList(editor, parentList); listDepth--) {
            parentList = $node.node(listDepth);
          }
          let listPos = 0;
          editor.view.state.doc.descendants((node, pos) => {
            if (node === parentList) listPos = pos;
          });
          // switch the list to the new type, or lift all its items out of it
          if (isList(editor, parentList) && parentList.type !== type) {
            if (listDepth >= 4 || $node.parent.childCount > 1) {
              const { tr } = editor.view.state;
              tr.setNodeMarkup(listPos + offset, type);
              editor.view.dispatch(tr);

              if ($node.nodeBefore?.type === type) {
                editor
                  .chain()
                  .setTextSelection(pos + offset + 1)
                  .joinBackward()
                  .run();
              }
            } else {
              liftListItem(editor, pos + offset);
              wrapSingleItemInList(editor, type, pos + offset);

              const $parent = editor.view.state.doc.resolve(pos);
              if ($parent.nodeBefore?.type === type) {
                editor
                  .chain()
                  .setTextSelection(pos + offset + 1)
                  .joinBackward()
                  .run();
              }
            }
          } else {
            liftListItem(editor, pos + offset);

            const $parent = editor.view.state.doc.resolve(pos);
            if ($parent.nodeBefore?.type === type) {
              editor
                .chain()
                .setTextSelection(pos + offset + 1)
                .joinBackward()
                .run();
            }
          }
        }
        const newDocSize = editor.view.state.doc.nodeSize;
        offset = offset + (newDocSize - originalDocSize);
      });
    }
  }
};

/**
 * Toggles a list type on/off or switches one list type to another
 * @param {NodeType} type the type of list to toggle
 */
const toggleList = (editor: Editor, type: NodeType) => {
  if (editor.view.state.selection instanceof CellSelection) {
    const LIST_ITEM_TYPE = editor.schema.nodes.listItem;
    const originalSelectionBox = selectedRect(editor.view.state);
    const selectedCellPositions: number[] = originalSelectionBox.map.cellsInRect(originalSelectionBox);
    const selectedCellIndices = selectedCellPositions.map((cellPos) =>
      originalSelectionBox.map.map.findIndex((pos) => pos === cellPos),
    );

    let toList = false;
    const textPositions: { cellPos: number; from: number; to: number }[] = [];

    selectedCellIndices.forEach((index) => {
      const selectionBox = selectedRect(editor.view.state);
      const currentCellPos = selectionBox.map.map[index] + selectionBox.tableStart;
      const cell = editor.view.state.doc.nodeAt(currentCellPos);
      let firstTextPos = -1;
      let lastTextPos = -1;

      // find the first and last text positions
      cell?.descendants((node: ProseMirrorNode, pos: number) => {
        if (node.isText && firstTextPos < 0) firstTextPos = pos;
        if (node.isText) lastTextPos = pos + node.nodeSize;
      });

      // if no text in cell, continue to next cell
      if (firstTextPos < 0) return;

      cell?.nodesBetween(firstTextPos, lastTextPos, (node, pos) => {
        if (node.type.name === 'paragraph') {
          const $pos = cell.resolve(pos);
          if ($pos.parent.type === LIST_ITEM_TYPE) return;
          toList = true;
        }
      });

      textPositions.push({ cellPos: currentCellPos, from: firstTextPos, to: lastTextPos });
    });

    let offset = 1;
    textPositions.forEach((position) => {
      const originalTableSize = editor.view.state.doc.child(0).nodeSize;
      const { cellPos, from, to } = position;
      // update selection to the text in this cell, and proceed with list toggle
      const newSelection = TextSelection.create(editor.view.state.doc, from + cellPos + offset, to + cellPos + offset);
      const tr = editor.view.state.tr;
      tr.setSelection(newSelection);
      tr.setMeta('addToHistory', false);

      editor.view.dispatch(tr);
      doToggleList(editor, type, toList);
      const newTableSize = editor.view.state.doc.child(0).nodeSize;
      offset = offset + (newTableSize - originalTableSize);
    });

    // reset the selection to the original cell selection
    const newSelectionBox = selectedRect(editor.view.state);
    const firstCellPos = newSelectionBox.map.map[selectedCellIndices[0]] + newSelectionBox.tableStart;
    const lastCellPos =
      newSelectionBox.map.map[selectedCellIndices[selectedCellIndices.length - 1]] + newSelectionBox.tableStart;
    editor.commands.setCellSelection({ anchorCell: firstCellPos, headCell: lastCellPos });
  } else {
    doToggleList(editor, type);
  }
  return true;
};

/**
 * Get selected closest table cell node
 *
 * @param selection - A prosemirror selection instance
 * @returns
 */
const getClosestCell = (selection: Selection): ReturnType<typeof findParentNodeClosestToPos> =>
  findParentNodeClosestToPos(selection.$anchor, (node: ProseMirrorNode) => {
    return node.type.name === TABLE_CELL_EXT_NAME || node.type.name === TABLE_HEADER_EXT_NAME;
  });

/**
 * Check whether specific row/column actions are disabled
 * {
 *  isAddColumnDisabled: TRUE if multiple columns are selected,
 *  isAddRowsDisabled: TRUE if multiple rows are selected,
 *  isDeleteColumnDisabled: TRUE if all columns are selected
 *  isDeleteRowDisabled: TRUE if all rows are selected
 * }
 *
 * @param editor Prosemirror editor
 * @returns
 */
const checkDisabledActions = (editor: Editor) => {
  const editorView = editor.view;
  if (!editorView)
    return {
      isAddColumnDisabled: false,
      isAddRowDisabled: false,
      isDeleteRowDisabled: false,
      isDeleteColumnDisabled: false,
    };

  const rect = selectedRect(editorView.state);
  const numSelectedRows = rect.bottom - rect.top;
  const numSelectedColumns = rect.right - rect.left;

  return {
    isAddColumnDisabled: numSelectedColumns > 1,
    isAddRowDisabled: numSelectedRows > 1,
    isDeleteRowDisabled: rect.map.height === 1 || numSelectedRows === rect.map.height,
    isDeleteColumnDisabled: rect.map.width === 1 || numSelectedColumns === rect.map.width,
  };
};

/**
 * Check if a position is within the current selection
 *
 * @param editor Prosemirror editor
 * @param pos Position of click event to check
 * @returns boolean - TRUE if pos is within the current selection
 */
const isPositionWithinCurrentSelection = (editor: Editor, pos: { top: number; left: number }) => {
  if (!editor) return false;

  // no selection
  if (editor.view.state.selection.empty) return false;

  const resolvedPos = editor.view.posAtCoords(pos)?.pos;
  if (!resolvedPos) return false;

  const selectionRanges = editor.view.state.selection.ranges;
  let isWithinSelection = false;
  selectionRanges.forEach(({ $from, $to }) => {
    if (isWithinSelection) return;
    isWithinSelection = resolvedPos >= $from.pos && resolvedPos <= $to.pos;
  });

  return isWithinSelection;
};

/**
 * Go backward from the passed selection position to find the nearest marks
 *
 * @param state Prosemirror editor state
 * @param position position in the editor
 * @param tableParentNodeType table parent node type (e.g. tableCell)
 * @param cellNode table cell node (to only look for marks in the same cell)
 */
const findBackwardMarks = (
  state: EditorState,
  position: number,
  tableParentNodeType: string | undefined,
  cellNode?: ProseMirrorNode | undefined,
) => {
  let marks: readonly Mark[] = [];

  for (let i = position; !marks.length && i >= 0; i--) {
    marks = getMarkFromPosition(state, i, tableParentNodeType, cellNode);
  }

  return marks;
};

/**
 * Go forward from the passed selection position to find the nearest marks
 *
 * @param state Prosemirror editor state
 * @param position position in the editor
 * @param tableParentNodeType table parent node type (e.g. tableCell)
 * @param cellNode table cell node (to only look for marks in the same cell
 */
const findForwardMarks = (
  state: EditorState,
  position: number,
  tableParentNodeType: string | undefined,
  cellNode?: ProseMirrorNode | undefined,
) => {
  let marks: readonly Mark[] = [];

  for (let i = position; !marks.length && i <= state.doc.content.size; i++) {
    marks = getMarkFromPosition(state, i, tableParentNodeType, cellNode);
  }

  return marks;
};

const textStyle = TEXT_TAG_TO_STYLES_MAP[TextWidgetTag.Paragraph];
const getDefaultMarks = (schema: Schema) => {
  return [
    schema.marks.textStyle.create({
      fontSize: `${textStyle.fontSize}px`,
      color: textStyle.color,
      fontFamily: textStyle.fontFamily,
    }),
  ];
};

/**
 * Get the marks from the node if it belongs to the same table node (e.g. cell can only have marks from the same cell)
 *
 * @param state Prosemirror editor state
 * @param position position in the editor
 * @param tableParentNodeType table parent node type (e.g. tableCell)
 * @param cellNode table cell node (to only look for marks in the same cell)
 */
const getMarkFromPosition = (
  state: EditorState,
  position: number,
  tableParentNodeType: string | undefined,
  cellNode?: ProseMirrorNode | undefined,
) => {
  const { doc } = state;
  const node = doc.nodeAt(position);
  if (!node) return [];

  const nodeMarks = node.marks;

  if (cellNode) {
    const nodeClosestCell = findParentNodeClosestToPos(state.doc.resolve(position), (node: ProseMirrorNode) => {
      return node === cellNode && node.type.name === tableParentNodeType;
    });
    if (nodeMarks && nodeClosestCell) return nodeMarks;
  }

  if (tableParentNodeType) {
    const nodeClosestTableparent = findParentNodeClosestToPos(
      state.doc.resolve(position),
      (node: ProseMirrorNode) => node.type.name === tableParentNodeType,
    );
    if (nodeMarks && nodeClosestTableparent) return nodeMarks;
  }

  return [];
};

/**
 * Searches text nodes for the nearest previous marks from the selection in the editor
 *
 * @param state Prosemirror editor state
 * @returns marks[]
 */
const getNearestMarksFromSelection = (state: EditorState) => {
  const { selection, doc, schema } = state;
  const { $from } = selection;

  const $node = doc.resolve($from.pos);
  const position = $node.pos;

  let marks: readonly Mark[] = [];

  // for tables get marks from the same table cell only
  const cellNode = getClosestCell(state.selection);
  const tableParentNodeType = cellNode?.node?.type.name;
  const isEmptyCell = cellNode?.node?.textContent.length === 0;

  // if the cell is empty look for marks from nearby cells with the same name (e.g. header only looks for marks in header)
  if (isEmptyCell) {
    marks = findBackwardMarks(state, position, tableParentNodeType);
    if (!marks.length) marks = findForwardMarks(state, position, tableParentNodeType);
    if (!marks.length) marks = getDefaultMarks(schema);
    return marks;
  }

  // if the cell is not empty look for marks from line above/below in the same cell
  marks = findBackwardMarks(state, position, tableParentNodeType, cellNode?.node);
  if (!marks.length) marks = findForwardMarks(state, position, tableParentNodeType, cellNode?.node);
  if (!marks.length) marks = getDefaultMarks(schema);

  return marks;
};

/**
 * Rescale table header and table cell.
 *
 * @param proseMirrorData table proseMirrorData.
 * @param ratio Rescale with this ratio.
 * @returns {JSONContent}
 */
const rescaleCells = (proseMirrorData: JSONContent, ratio: number): JSONContent => {
  const proseClone = cloneDeep(proseMirrorData);
  return traverseRescaleTableCell(proseClone, ratio);
};

/**
 * Rescale cells recursive call under the hood, mutating params.
 *
 * @param proseMirrorData
 * @param ratio
 * @returns
 */
const traverseRescaleTableCell = (proseMirrorData: JSONContent, ratio: number): JSONContent => {
  let content = proseMirrorData.content;
  if (!content) {
    return proseMirrorData;
  }

  for (let i = 0; i < (content?.length ?? 0); i++) {
    if (content?.[i].type === 'tableHeader' || content?.[i].type === 'tableCell') {
      if (content?.[i].attrs?.colwidth[0]) {
        content[i].attrs!.colwidth[0] = content[i].attrs?.colwidth[0] * ratio;
      }
    } else if (content?.[i].type === 'paragraph') {
      const numberLineHeight = Number(content?.[i].attrs?.lineHeight);
      if (Boolean(numberLineHeight)) {
        // assign back as string like initial value
        content[i].attrs!.lineHeight = (numberLineHeight * ratio).toFixed(1);
      }
    }

    if (content?.[i]) {
      traverseRescaleTableCell(content?.[i], ratio);
    }
  }

  return proseMirrorData;
};

/**
 * Updates the table cell colors and text colors in the provided ProseMirror JSONContent.
 * OBS: the new table colors must be provided in hex format.
 *
 * @param {JSONContent} proseMirrorData - The ProseMirror JSON content to be updated.
 * @param {object} tableColors - An object mapping background colors to their new cell and text colors.
 * @param {string} [cellTextColor='#000000'] - The default text color to be applied to cells.
 * @param {boolean} [inPlace=false] - If true, modifies the original data in place; otherwise, clones the data before modification.
 * @returns {JSONContent} The updated ProseMirror JSON content with new colors.
 */
const updateTableColor = (
  proseMirrorData: JSONContent,
  tableColors: { [oldCellColor: string]: { cellColor: string; textColor: string } },
  cellTextColor = '#000000',
  inPlace = false,
): JSONContent => {
  const newPmData = inPlace ? proseMirrorData : cloneDeep(proseMirrorData);
  let textColor = cellTextColor;

  newPmData.content?.forEach((content) => {
    if (content.attrs && 'color' in content.attrs) {
      content.attrs.color = textColor;
    }

    if (content.attrs && 'backgroundColor' in content.attrs) {
      const backgroundColor = toHex(content.attrs.backgroundColor);
      const newColor = tableColors[backgroundColor];

      if (newColor && newColor.cellColor && newColor.textColor) {
        const newCellColor = newColor.cellColor;
        content.attrs.backgroundColor = newCellColor;
        const newTextColor = newColor.textColor;
        textColor = newTextColor;
      } else {
        console.warn('No color found for', backgroundColor, 'in tableColors', tableColors);
      }
    }

    const marks = content.marks;
    if (marks) {
      const textStyleIdx = marks.findIndex((mark) => mark.type === 'textStyle');
      if (textStyleIdx > -1) {
        const textStyleMarks = marks[textStyleIdx];
        if (textStyleMarks.attrs) {
          textStyleMarks.attrs.color = textColor;
        }
      }
    }

    if (content.content) {
      return updateTableColor(content, tableColors, textColor, true);
    }
  });

  return newPmData;
};

/**
 * Get all the cell colors for the table
 *
 * @param proseMirrorData
 * @returns
 */
const getTableCellColors = (proseMirrorData: JSONContent, colors: string[] = []) => {
  // Check if the current node has content to process further
  if (proseMirrorData.content) {
    proseMirrorData.content.forEach((contentItem) => {
      // If the current item is a table cell with a background color, add it to the array
      if (contentItem.attrs && contentItem.attrs.backgroundColor) colors.push(contentItem.attrs.backgroundColor);

      // Recurse into nested content if it exists
      if (contentItem.content) {
        getTableCellColors(contentItem, colors);
      }
    });
  }

  // return only unique colors
  return Array.from(new Set(colors));
};

const fireMPonTableInsert = (insertType: 'Column' | 'Row') => {
  Mixpanel.track(TABLE_EDITED.eventName, {
    insert_type: TABLE_EDITED.insert_type[insertType],
  });
};
const fireMPonTableDelete = (deleteType: 'Column' | 'Row') => {
  Mixpanel.track(TABLE_EDITED.eventName, {
    delete_type: TABLE_EDITED.delete_type[deleteType],
  });
};

function hasCheckbox(node: JSONContent): boolean {
  if (node.type === 'checkbox') {
    return true;
  }

  if (node.content && Array.isArray(node.content)) {
    for (const child of node.content) {
      if (hasCheckbox(child)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Given a proseMirrorNode table data, check if it's an interactive list or not
 *
 * @param tableData JSONContent
 * @returns
 */
function checkForCheckbox(tableData: string | JSONContent): boolean {
  if (typeof tableData === 'object' && tableData.content && Array.isArray(tableData.content)) {
    for (const node of tableData.content) {
      if (hasCheckbox(node)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Update a proseMirrorData in place with the new checked state
 *
 * @param node JSONContent
 * @param checkboxId nanonid for checkbox field
 * @param isChecked bool
 * @returns An updated JSONContent in place with the checkbox checked or unchecked
 */
function updateCheckboxByAttrId(node: JSONContent, checkboxId: string, isChecked: boolean): JSONContent {
  if (node?.content && Array.isArray(node.content)) {
    node.content.map((child) => updateCheckboxByAttrId(child, checkboxId, isChecked));
  }

  if (node?.type === 'checkbox' && node?.attrs?.id === checkboxId) {
    node.attrs.isChecked = isChecked;
  }

  return node;
}

export {
  getAllTextContents,
  getTableCellColors,
  getDominantTextStyleByColor,
  getTableData,
  updateAllCellBorderStyle,
  updateCellAttrs,
  updateColumnAttrsLeft,
  updateColumnAttrsRight,
  updateRowAttrsBelow,
  updateRowAttrsAbove,
  updateTableColor,
  getClosestCellAttributes,
  handlePaste,
  toggleList,
  isList,
  getClosestCell,
  checkDisabledActions,
  isPositionWithinCurrentSelection,
  getNearestMarksFromSelection,
  rescaleCells,
  fireMPonTableInsert,
  fireMPonTableDelete,
  checkForCheckbox,
  updateCheckboxByAttrId,
};
